import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import {
  useSignIn,
  useAuth,
  useUser as useClerkUser,
} from '@clerk/clerk-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

import api from '../utils/api';
import { useUser } from '../context/UserContext';

AuthPage.propTypes = {
  isLogin: PropTypes.bool,
};

AuthPage.defaultProps = {
  isLogin: true,
};

export default function AuthPage({ isLogin = true }) {
  const navigate = useNavigate();
  const { rehydrateUserContext } = useUser();
  const { isSignedIn } = useAuth();
  const { user: clerkUser } = useClerkUser();
  const { signIn } = useSignIn();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal states
  const [showAccountNotFoundModal, setShowAccountNotFoundModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);

  // Signup OTP state
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [otpTimer, setOtpTimer] = useState(30);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendSuccess, setResendSuccess] = useState('');

  // Redirect if signed in
  useEffect(() => {
    if (isSignedIn && clerkUser) {
      // Sync Clerk user
      const syncWithBackend = async () => {
        try {
          // Sync with backend
          const response = await api.post('/auth/clerk-sync', {
            clerkUserId: clerkUser.id,
            email: clerkUser.emailAddresses[0]?.emailAddress,
            name: clerkUser.fullName || clerkUser.firstName || 'User',
            profileImage: clerkUser.imageUrl,
          });

          if (response.data.token) {
            // Store token
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userId', response.data.user.id);
            localStorage.setItem('userName', response.data.user.name);

            // Update context
            rehydrateUserContext(response.data.user, true);

            // Navigate to dashboard
            navigate('/dashboard');
          }
        } catch (syncError) {
          // console.error('Failed to sync with backend:', syncError);
          // Clear Clerk session if backend sync fails
          // await signOut();
          setError('Failed to complete sign-in. Please try again.');
        }
      };

      syncWithBackend();
    }
  }, [isSignedIn, clerkUser, navigate, rehydrateUserContext]);

  // Forgot Password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotOtpLoading, setForgotOtpLoading] = useState(false);
  const [forgotOtpError, setForgotOtpError] = useState('');
  const [forgotOtpSuccess, setForgotOtpSuccess] = useState('');
  const [forgotOtpTimer, setForgotOtpTimer] = useState(30);
  const [forgotResendLoading, setForgotResendLoading] = useState(false);
  const [forgotResendError, setForgotResendError] = useState('');
  const [forgotResendSuccess, setForgotResendSuccess] = useState('');

  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotResetLoading, setForgotResetLoading] = useState(false);
  const [forgotResetError, setForgotResetError] = useState('');
  const [forgotResetSuccess, setForgotResetSuccess] = useState('');

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError(''); // Clear error when user starts typing
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Validate password
  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters long';
    if (/\s/.test(password)) return 'Password cannot contain spaces or whitespace characters';
    return null;
  };

  // Handle Login/Signup
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate password
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        setError(passwordError);
        setLoading(false);
        return;
      }

      if (isLogin) {
        // Login
        const res = await api.post('/auth/login', {
          emailOrUsername: formData.email,
          password: formData.password,
        });
        const { data } = res;

        // If there's an error, axios throws; catch will handle

        // Store auth data
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('userName', data.user?.name || '');
        localStorage.setItem('userId', data.user?.id || data.user?._id || '');

        if (data.user) {
          rehydrateUserContext(data.user, true);
        }
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 400);
      } else {
        // Signup validation
        if (!formData.email.includes('@') || !formData.email.includes('.')) {
          setError('Invalid email format.');
          setLoading(false);
          return;
        }
        if (
          formData.name.trim().length < 2
          || formData.name.trim().length > 50
        ) {
          setError('Name must be between 2 and 50 characters.');
          setLoading(false);
          return;
        }
        if (
          formData.username.trim().length < 3
          || formData.username.trim().length > 30
        ) {
          setError('Username must be between 3 and 30 characters.');
          setLoading(false);
          return;
        }
        // Password validation is already done above, but keep this as backup
        const signupPasswordError = validatePassword(formData.password);
        if (signupPasswordError) {
          setError(signupPasswordError);
          setLoading(false);
          return;
        }
        // Signup API
        await api.post('/auth/register', {
          name: formData.name,
          username: formData.username.toLowerCase(),
          email: formData.email.toLowerCase(),
          password: formData.password,
        });

        // Show OTP modal
        setShowOtpModal(true);
      }
    } catch (err) {
      setError(
        err.response?.data?.message
        || err.message
        || 'Failed to connect to the server.',
      );
    } finally {
      setLoading(false);
    }
  };

  // Verify Signup OTP
  const handleOtpVerification = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    setOtpError('');
    setOtpSuccess('');
    try {
      const res = await api.post('/auth/verify-otp', {
        email: formData.email.toLowerCase(),
        otp,
        instantLogin: true,
      });
      const { data } = res;

      // Handle successful verification
      if (data.accessToken) {
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('userName', data.user?.name || '');
        localStorage.setItem('userId', data.user?.id || data.user?._id || '');
        if (data.user) {
          rehydrateUserContext(data.user, false); // Don't refetch - we already have complete data
        }
        setOtpSuccess('Email verified! Logging you in...');
        setTimeout(() => {
          setShowOtpModal(false);
          navigate('/dashboard');
        }, 1500);
      } else {
        // Manual login required
        setOtpSuccess('Email verified! You can now log in.');
        setTimeout(() => {
          setShowOtpModal(false);
          navigate('/login');
        }, 1500);
      }
    } catch (err) {
      setOtpError(
        err.response?.data?.message
        || err.message
        || 'Failed to connect to the server.',
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend Signup OTP
  const handleResendOtp = async () => {
    setResendLoading(true);
    setResendError('');
    setResendSuccess('');
    try {
      // Resend OTP
      await api.post('/auth/register', {
        name: formData.name,
        username: formData.username.toLowerCase(),
        email: formData.email.toLowerCase(),
        password: formData.password,
      });
      setResendSuccess('OTP resent! Check your email.');
      setOtpTimer(30); // Reset timer
    } catch (err) {
      setResendError(
        err.response?.data?.message
        || err.message
        || 'Failed to connect to the server.',
      );
    } finally {
      setResendLoading(false);
    }
  };

  // Forgot Password: Send OTP
  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    setForgotSuccess('');
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail.toLowerCase() });
      setForgotSuccess('OTP sent! Check your email.');
      setForgotStep(2);
    } catch (err) {
      setForgotError(
        err.response?.data?.message
        || err.message
        || 'Failed to connect to the server.',
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password: Verify OTP
  const handleForgotVerifyOtp = async (e) => {
    e.preventDefault();
    setForgotOtpLoading(true);
    setForgotOtpError('');
    setForgotOtpSuccess('');
    try {
      await api.post('/auth/verify-reset-otp', {
        email: forgotEmail.toLowerCase(),
        otp: forgotOtp,
      });
      setForgotOtpSuccess('OTP verified!');
      setTimeout(() => setForgotStep(3), 500);
    } catch (err) {
      setForgotOtpError(
        err.response?.data?.message
        || err.message
        || 'Failed to connect to the server.',
      );
    } finally {
      setForgotOtpLoading(false);
    }
  };

  // Forgot Password: Resend OTP
  const handleForgotResendOtp = async () => {
    setForgotResendLoading(true);
    setForgotResendError('');
    setForgotResendSuccess('');
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail.toLowerCase() });
      setForgotResendSuccess('OTP resent! Check your email.');
      setForgotOtpTimer(30);
    } catch (err) {
      setForgotResendError(
        err.response?.data?.message
        || err.message
        || 'Failed to connect to the server.',
      );
    } finally {
      setForgotResendLoading(false);
    }
  };

  // Forgot Password: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotResetLoading(true);
    setForgotResetError('');
    setForgotResetSuccess('');

    // Validate new password
    const passwordError = validatePassword(forgotNewPassword);
    if (passwordError) {
      setForgotResetError(passwordError);
      setForgotResetLoading(false);
      return;
    }

    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotResetError('Passwords do not match.');
      setForgotResetLoading(false);
      return;
    }
    try {
      await api.post('/auth/reset-password', {
        email: forgotEmail.toLowerCase(),
        otp: forgotOtp,
        newPassword: forgotNewPassword,
      });
      setForgotResetSuccess('Password reset successful! You can now log in.');
      setTimeout(() => {
        setShowForgotModal(false);
        setForgotStep(1); // Reset forgot password flow
        // Clear all forgot password states
        setForgotEmail('');
        setForgotOtp('');
        setForgotNewPassword('');
        setForgotConfirmPassword('');
        setForgotError('');
        setForgotSuccess('');
        setForgotOtpError('');
        setForgotOtpSuccess('');
        setForgotResendError('');
        setForgotResendSuccess('');
        setForgotResetError('');
        setForgotResetSuccess('');
        navigate('/login');
      }, 1500);
    } catch (err) {
      setForgotResetError(
        err.response?.data?.message
        || err.message
        || 'Failed to connect to the server.',
      );
    } finally {
      setForgotResetLoading(false);
    }
  };

  // Signup OTP timer
  useEffect(() => {
    if (!showOtpModal) return;
    if (otpTimer === 0) return;
    const interval = setInterval(() => {
      setOtpTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [showOtpModal, otpTimer]);

  // Reset Signup OTP state
  useEffect(() => {
    if (showOtpModal) {
      setOtpTimer(30);
      setResendError('');
      setResendSuccess('');
      setOtpError('');
      setOtpSuccess('');
      setOtp(''); // Clear OTP input on modal open
    }
  }, [showOtpModal]);

  // Forgot Password OTP timer
  useEffect(() => {
    if (!showForgotModal || forgotStep !== 2) return;
    if (forgotOtpTimer === 0) return;
    const interval = setInterval(() => {
      setForgotOtpTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [showForgotModal, forgotStep, forgotOtpTimer]);

  // Reset Forgot Password state
  useEffect(() => {
    if (showForgotModal) {
      if (forgotStep === 1) {
        setForgotEmail('');
        setForgotError('');
        setForgotSuccess('');
      } else if (forgotStep === 2) {
        setForgotOtpTimer(30);
        setForgotResendError('');
        setForgotResendSuccess('');
        setForgotOtpError('');
        setForgotOtpSuccess('');
        setForgotOtp(''); // Clear OTP input on step change
      } else if (forgotStep === 3) {
        setForgotNewPassword('');
        setForgotConfirmPassword('');
        setForgotResetError('');
        setForgotResetSuccess('');
      }
    }
  }, [showForgotModal, forgotStep]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 font-inter">
      <div className="w-full max-w-md sm:max-w-md md:max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-6">
          <img
            src="/logos/logo.png"
            alt="Sublite Logo"
            className="w-12 h-12 rounded-full"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/48x48/2bb6c4/ffffff?text=SL';
            }}
          />
          <span className="text-2xl font-extrabold text-[#2bb6c4] tracking-tight">
            Sublite
          </span>
        </div>
        <h2 className="text-2xl font-bold text-center text-gray-800 dark:text-white mb-1">
          {isLogin ? 'Welcome Back' : 'Create Account'}
        </h2>
        <p className="text-center text-gray-500 dark:text-gray-300 mb-6 text-sm">
          {isLogin ? 'Please enter your details' : 'Sign up to get started'}
        </p>

        {/* Google Sign In Section */}
        <div className="mb-4">
          <button
            type="button"
            onClick={async () => {
              try {
                setLoading(true);
                setError('');

                if (isSignedIn) {
                  navigate('/dashboard');
                  return;
                }

                if (!signIn) {
                  setError('Authentication service not ready. Please refresh the page.');
                  return;
                }

                await signIn.authenticateWithRedirect({
                  strategy: 'oauth_google',
                  redirectUrl: `${window.location.origin}/sso-callback`,
                  redirectUrlComplete: `${window.location.origin}/sso-callback`,
                });
              } catch (err) {
                if (err.errors?.[0]?.code === 'session_exists' || err.message?.includes('already signed in')) {
                  navigate('/dashboard');
                } else {
                  setError(err.errors?.[0]?.message || 'Google sign-in failed. Please try again.');
                }
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full py-3 px-4 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center gap-3 font-medium text-gray-700 dark:text-gray-300 shadow-sm"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            Continue with Google
          </button>
        </div>

        {/* Or Divider */}
        <div className="relative mb-5">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-300 dark:border-gray-600" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white dark:bg-gray-800 px-4 text-gray-500 dark:text-gray-400 font-medium">
              Or
            </span>
          </div>
        </div>

        {/* Sign In/Sign Up Tabs */}
        <div className="flex mb-5 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600">
          <Button
            asChild
            className={`flex-1 py-2 font-semibold text-center rounded-none transition-all duration-200 ${isLogin ? 'bg-[#2bb6c4] text-white shadow-md' : 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'} shadow-none border-none`}
          >
            <Link to="/login">Sign In</Link>
          </Button>
          <Button
            asChild
            className={`flex-1 py-2 font-semibold text-center rounded-none transition-all duration-200 ${!isLogin ? 'bg-[#2bb6c4] text-white shadow-md' : 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'} shadow-none border-none`}
          >
            <Link to="/register">Sign up</Link>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <label
                  htmlFor="fullName"
                  className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Full Name
                </label>
                <Input
                  id="fullName"
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  className="px-4 py-3 rounded-xl border border-gray-400 dark:border-gray-500 outline-none transition-all duration-200 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Username
                </label>
                <Input
                  id="username"
                  type="text"
                  name="username"
                  placeholder="Username"
                  className="px-4 py-3 rounded-xl border border-gray-400 dark:border-gray-500 outline-none transition-all duration-200 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </div>
            </>
          )}
          <div>
            <label
              htmlFor="emailOrUsername"
              className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1"
            >
              {isLogin ? 'Email or Username' : 'Email Address'}
            </label>
            <Input
              id="emailOrUsername"
              type={isLogin ? 'text' : 'email'}
              name="email"
              placeholder={isLogin ? 'Email or Username' : 'Email Address'}
              className="px-4 py-3 rounded-xl border border-gray-400 dark:border-gray-500 outline-none transition-all duration-200 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
              value={formData.email}
              onChange={handleChange}
              required
              autoFocus
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                className="px-4 py-3 rounded-xl border border-gray-400 dark:border-gray-500 outline-none transition-all duration-200 pr-10 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-0 top-0 h-full bg-transparent border-none cursor-pointer px-4 py-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          {isLogin && (
            <div className="flex justify-end mb-2">
              <button
                type="button"
                className="text-sm text-[#2bb6c4] hover:underline font-semibold focus:outline-none"
                onClick={() => setShowForgotModal(true)}
              >
                Forgot Password?
              </button>
            </div>
          )}
          <Button
            type="submit"
            className="w-full bg-[#2bb6c4] hover:bg-[#1ea1b0] text-white py-2 rounded-xl font-semibold shadow-lg transition duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading
              ? isLogin
                ? 'Logging in...'
                : 'Signing Up...'
              : isLogin
                ? 'Continue'
                : 'Sign Up'}
          </Button>
        </form>
        {error && <div className="text-red-500 text-center mt-4">{error}</div>}
        <div className="text-center text-gray-400 text-xs mt-4">
          {isLogin ? (
            <>
              Don&apos;t have an account?
              {' '}
              <Link
                to="/register"
                className="text-[#2bb6c4] hover:underline font-semibold"
              >
                Sign Up
              </Link>
            </>
          ) : (
            <>
              Already have an account?
              {' '}
              <Link
                to="/login"
                className="text-[#2bb6c4] hover:underline font-semibold"
              >
                Sign In
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Account Not Found Modal */}
      {
        showAccountNotFoundModal && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-60 z-50"
            aria-modal="true"
            role="dialog"
          >
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 text-center w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
                No Account Found
              </h2>
              <p className="mb-4 text-gray-600 dark:text-gray-300">
                Would you like to sign up instead?
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  onClick={() => {
                    setShowAccountNotFoundModal(false);
                    navigate('/register');
                  }}
                  className="bg-[#2bb6c4] hover:bg-[#1ea1b0] text-white rounded-xl px-4 py-2 shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Sign Up
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowAccountNotFoundModal(false)}
                  className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl px-4 py-2 transition-all duration-200"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Forgot Password Modal */}
      {
        showForgotModal && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-60 z-50"
            aria-modal="true"
            role="dialog"
          >
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-sm">
              {forgotStep === 1 && (
                <>
                  <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">
                    Forgot Password
                  </h2>
                  <form onSubmit={handleForgotSendOtp}>
                    <div className="mb-4">
                      <label
                        htmlFor="forgotEmail"
                        className="block mb-1 text-gray-600 dark:text-gray-300"
                      >
                        Email
                      </label>
                      <Input
                        id="forgotEmail"
                        type="email"
                        className="w-full border rounded-xl px-3 py-2 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                      />
                    </div>
                    {forgotError && (
                      <div className="text-red-500 mb-2 text-sm">
                        {forgotError}
                      </div>
                    )}
                    {forgotSuccess && (
                      <div className="text-green-500 mb-2 text-sm">
                        {forgotSuccess}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <Button
                        type="button"
                        variant="outline"
                        className="text-gray-500 hover:underline border-none"
                        onClick={() => {
                          setShowForgotModal(false);
                          setForgotStep(1);
                        }}
                        disabled={forgotLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-[#2bb6c4] hover:bg-[#1ea1b0] text-white px-4 py-2 rounded-xl"
                        disabled={forgotLoading}
                      >
                        {forgotLoading ? 'Sending...' : 'Send OTP'}
                      </Button>
                    </div>
                  </form>
                </>
              )}

              {forgotStep === 2 && (
                <>
                  <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">
                    Verify OTP
                  </h2>
                  <form onSubmit={handleForgotVerifyOtp}>
                    <div className="mb-4">
                      <label
                        htmlFor="forgotOtp"
                        className="block mb-1 text-gray-600 dark:text-gray-300"
                      >
                        OTP
                      </label>
                      <Input
                        id="forgotOtp"
                        type="text"
                        className="w-full border rounded-xl px-3 py-2 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
                        value={forgotOtp}
                        onChange={(e) => setForgotOtp(e.target.value)}
                        required
                        maxLength={6}
                      />
                    </div>
                    {forgotOtpError && (
                      <div className="text-red-500 mb-2 text-sm">
                        {forgotOtpError}
                      </div>
                    )}
                    {forgotOtpSuccess && (
                      <div className="text-green-500 mb-2 text-sm">
                        {forgotOtpSuccess}
                      </div>
                    )}
                    {forgotResendError && (
                      <div className="text-red-500 mb-2 text-sm">
                        {forgotResendError}
                      </div>
                    )}
                    {forgotResendSuccess && (
                      <div className="text-green-500 mb-2 text-sm">
                        {forgotResendSuccess}
                      </div>
                    )}
                    <div className="flex justify-between items-center mb-4">
                      <button
                        type="button"
                        className={`text-[#2bb6c4] font-semibold text-sm ${forgotOtpTimer > 0 || forgotResendLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={forgotOtpTimer > 0 || forgotResendLoading}
                        onClick={handleForgotResendOtp}
                      >
                        Resend OTP
                        {' '}
                        {forgotOtpTimer > 0 && `(${forgotOtpTimer}s)`}
                      </button>
                    </div>
                    <div className="flex justify-between items-center">
                      <Button
                        type="button"
                        variant="outline"
                        className="text-gray-500 hover:underline border-none"
                        onClick={() => {
                          setShowForgotModal(false);
                          setForgotStep(1);
                        }}
                        disabled={forgotOtpLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-[#2bb6c4] hover:bg-[#1ea1b0] text-white px-4 py-2 rounded-xl"
                        disabled={forgotOtpLoading}
                      >
                        {forgotOtpLoading ? 'Verifying...' : 'Verify OTP'}
                      </Button>
                    </div>
                  </form>
                </>
              )}

              {forgotStep === 3 && (
                <>
                  <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">
                    Reset Password
                  </h2>
                  <form onSubmit={handleResetPassword}>
                    <div className="mb-4">
                      <label
                        htmlFor="newPassword"
                        className="block mb-1 text-gray-600 dark:text-gray-300"
                      >
                        New Password
                      </label>
                      <Input
                        id="newPassword"
                        type="password"
                        className="w-full border rounded-xl px-3 py-2 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
                        value={forgotNewPassword}
                        onChange={(e) => setForgotNewPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label
                        htmlFor="confirmPassword"
                        className="block mb-1 text-gray-600 dark:text-gray-300"
                      >
                        Confirm Password
                      </label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        className="w-full border rounded-xl px-3 py-2 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
                        value={forgotConfirmPassword}
                        onChange={(e) => setForgotConfirmPassword(e.target.value)}
                        required
                      />
                    </div>
                    {forgotResetError && (
                      <div className="text-red-500 mb-2 text-sm">
                        {forgotResetError}
                      </div>
                    )}
                    {forgotResetSuccess && (
                      <div className="text-green-500 mb-2 text-sm">
                        {forgotResetSuccess}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <Button
                        type="button"
                        variant="outline"
                        className="text-gray-500 hover:underline border-none"
                        onClick={() => {
                          setShowForgotModal(false);
                          setForgotStep(1);
                        }}
                        disabled={forgotResetLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-[#2bb6c4] hover:bg-[#1ea1b0] text-white px-4 py-2 rounded-xl"
                        disabled={forgotResetLoading}
                      >
                        {forgotResetLoading ? 'Resetting...' : 'Reset Password'}
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )
      }

      {/* Signup OTP Verification Modal */}
      {
        showOtpModal && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-60 z-50"
            aria-modal="true"
            role="dialog"
          >
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 text-center w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
                Verify Email
              </h2>
              <p className="mb-4 text-gray-600 dark:text-gray-300">
                Enter the OTP sent to your email address:
                {' '}
                <span className="font-semibold text-[#2bb6c4] dark:text-[#5ed1dc]">
                  {formData.email}
                </span>
              </p>
              <form onSubmit={handleOtpVerification}>
                <div className="mb-4">
                  <label
                    htmlFor="signupOtp"
                    className="block mb-1 text-gray-600 dark:text-gray-300"
                  >
                    OTP
                  </label>
                  <Input
                    id="signupOtp"
                    type="text"
                    className="w-full border rounded-xl px-3 py-2 focus:border-[#2bb6c4] focus:ring-1 focus:ring-[#2bb6c4]"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    maxLength={6}
                  />
                </div>
                {otpError && (
                  <div className="text-red-500 mb-2 text-sm">{otpError}</div>
                )}
                {otpSuccess && (
                  <div className="text-green-500 mb-2 text-sm">{otpSuccess}</div>
                )}
                {resendError && (
                  <div className="text-red-500 mb-2 text-sm">{resendError}</div>
                )}
                {resendSuccess && (
                  <div className="text-green-500 mb-2 text-sm">
                    {resendSuccess}
                  </div>
                )}
                <div className="flex justify-between items-center mb-4">
                  <button
                    type="button"
                    className={`text-[#2bb6c4] font-semibold text-sm ${otpTimer > 0 || resendLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={otpTimer > 0 || resendLoading}
                    onClick={handleResendOtp}
                  >
                    Resend OTP
                    {' '}
                    {otpTimer > 0 && `(${otpTimer}s)`}
                  </button>
                </div>
                <div className="flex justify-between items-center">
                  <Button
                    type="button"
                    variant="outline"
                    className="text-gray-500 hover:underline border-none"
                    onClick={() => setShowOtpModal(false)}
                    disabled={otpLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#2bb6c4] hover:bg-[#1ea1b0] text-white px-4 py-2 rounded-xl"
                    disabled={otpLoading}
                  >
                    {otpLoading ? 'Verifying...' : 'Verify'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )
      }
    </div>
  );
}
